/** @format */

import React from 'react'
import styled from 'styled-components'
import { _$, Button, Card, Message } from '@king-design/react'
import { useParams, useHistory } from 'react-router'
import { CustomPageContainer } from 'src/components'
import { getFunctionDetail } from 'src/api/ApiEventOpsFunction'
import { applyAudit } from 'src/api/ApiEventOpsFunction'
import { useAsync } from 'react-use'
import ExampleCodeViewer from '../ExampleCodeViewer'
import { decodeExample } from '../utils'

const Approve: React.FC<{ className?: string }> = ({ className }) => {
  const params = useParams<{ ID: string }>()
  const ID = params.ID
  const history = useHistory()

  const { value: functionDetail } = useAsync(async () => {
    if (ID) {
      const parsedId = parseInt(ID)
      if (!isNaN(parsedId)) {
        // 审批页面始终查看指定版本
        return getFunctionDetail({
          ID: parsedId,
          Latest: false
        })
      }
    }
    return null
  }, [ID])

  // 获取当前审批状态
  const approving = functionDetail?.Approving

  // 审批
  const onApplyAudit = async (Status: -2 | -1 | 2) => {
    const result = await applyAudit({
      Module: 'script',
      TargetID: parseInt(ID),
      Status
    })
    if (result?.code === 'Success') {
      if (Status === -2) {
        Message.success(_$('审批撤回成功'))
      } else if (Status === -1) {
        Message.success(_$('审批驳回成功'))
      } else if (Status === 2) {
        Message.success(_$('审批通过成功'))
      }
      history.push(`/monitor/eventops/functions`)
    }
  }

  const codeMode = functionDetail?.Lang === 'shell' ? 'shell' : 'python'
  const paramsList = functionDetail?.Extra?.Params || functionDetail?.Extra?.params || []
  const variables = functionDetail?.Extra?.Variables || functionDetail?.Extra?.variables || []
  const example = decodeExample(functionDetail?.Extra?.Example)

  return (
    <div className={className}>
      <CustomPageContainer title={_$('审批详情')} showBack>
        <Card className="card_body" style={{ marginBottom: 16 }}>
          <div style={{ padding: '16px' }}>
            {/* 基本信息 */}
            <div style={{ marginBottom: 24 }}>
              <h3 style={{ fontSize: '16px', fontWeight: 500, marginBottom: 16 }}>
                {_$('基本信息')}
              </h3>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                <div>
                  <span style={{ fontSize: '14px', color: '#666', marginRight: 8 }}>
                    {_$('函数名称')}:
                  </span>
                  <span style={{ fontSize: '14px', color: '#333' }}>
                    {functionDetail?.Sign || '-'}
                  </span>
                </div>
                <div>
                  <span style={{ fontSize: '14px', color: '#666', marginRight: 8 }}>
                    {_$('语言类型')}:
                  </span>
                  <span style={{ fontSize: '14px', color: '#333' }}>
                    {functionDetail?.Lang === 'shell' ? 'Shell' : 'Python3'}
                  </span>
                </div>
                {functionDetail?.Remark && (
                  <div>
                    <span style={{ fontSize: '14px', color: '#666', marginRight: 8 }}>
                      {_$('备注')}:
                    </span>
                    <span style={{ fontSize: '14px', color: '#333' }}>{functionDetail.Remark}</span>
                  </div>
                )}
              </div>
            </div>

            {/* 参数 */}
            {paramsList.length > 0 && (
              <div style={{ marginBottom: 24 }}>
                <h3 style={{ fontSize: '16px', fontWeight: 500, marginBottom: 16 }}>
                  {_$('参数')}
                </h3>
                <table
                  style={{
                    width: '100%',
                    borderCollapse: 'collapse',
                    fontSize: '14px'
                  }}
                >
                  <thead>
                    <tr style={{ backgroundColor: '#fafafa' }}>
                      <th
                        style={{
                          padding: '8px 12px',
                          textAlign: 'left',
                          border: '1px solid #e8e8e8'
                        }}
                      >
                        {_$('参数名')}
                      </th>
                      <th
                        style={{
                          padding: '8px 12px',
                          textAlign: 'left',
                          border: '1px solid #e8e8e8'
                        }}
                      >
                        {_$('类型')}
                      </th>
                      <th
                        style={{
                          padding: '8px 12px',
                          textAlign: 'left',
                          border: '1px solid #e8e8e8'
                        }}
                      >
                        {_$('默认值')}
                      </th>
                      <th
                        style={{
                          padding: '8px 12px',
                          textAlign: 'left',
                          border: '1px solid #e8e8e8'
                        }}
                      >
                        {_$('必填')}
                      </th>
                      <th
                        style={{
                          padding: '8px 12px',
                          textAlign: 'left',
                          border: '1px solid #e8e8e8'
                        }}
                      >
                        {_$('备注')}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {paramsList.map((param: any, index: number) => (
                      <tr key={index}>
                        <td style={{ padding: '8px 12px', border: '1px solid #e8e8e8' }}>
                          {param.Key || param.key || '-'}
                        </td>
                        <td style={{ padding: '8px 12px', border: '1px solid #e8e8e8' }}>
                          {param.Type || param.type || '-'}
                        </td>
                        <td style={{ padding: '8px 12px', border: '1px solid #e8e8e8' }}>
                          {param.Default || param.default || '-'}
                        </td>
                        <td style={{ padding: '8px 12px', border: '1px solid #e8e8e8' }}>
                          {param.Required || param.required ? _$('是') : _$('否')}
                        </td>
                        <td style={{ padding: '8px 12px', border: '1px solid #e8e8e8' }}>
                          {param.Remark || param.remark || '-'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* 特殊变量 */}
            {variables.length > 0 && (
              <div style={{ marginBottom: 24 }}>
                <h3 style={{ fontSize: '16px', fontWeight: 500, marginBottom: 16 }}>
                  {_$('特殊变量')}
                </h3>
                <table
                  style={{
                    width: '100%',
                    borderCollapse: 'collapse',
                    fontSize: '14px'
                  }}
                >
                  <thead>
                    <tr style={{ backgroundColor: '#fafafa' }}>
                      <th
                        style={{
                          padding: '8px 12px',
                          textAlign: 'left',
                          border: '1px solid #e8e8e8'
                        }}
                      >
                        {_$('变量名')}
                      </th>
                      <th
                        style={{
                          padding: '8px 12px',
                          textAlign: 'left',
                          border: '1px solid #e8e8e8'
                        }}
                      >
                        {_$('变量值')}
                      </th>
                      <th
                        style={{
                          padding: '8px 12px',
                          textAlign: 'left',
                          border: '1px solid #e8e8e8'
                        }}
                      >
                        {_$('加密')}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {variables.map((variable: any, index: number) => (
                      <tr key={index}>
                        <td style={{ padding: '8px 12px', border: '1px solid #e8e8e8' }}>
                          {variable.Key || variable.key || '-'}
                        </td>
                        <td style={{ padding: '8px 12px', border: '1px solid #e8e8e8' }}>
                          {variable.IsSecret || variable.isSecret
                            ? '***'
                            : variable.Value || variable.value || '-'}
                        </td>
                        <td style={{ padding: '8px 12px', border: '1px solid #e8e8e8' }}>
                          {variable.IsSecret || variable.isSecret ? _$('是') : _$('否')}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* 脚本内容 */}
            {functionDetail?.Content && (
              <div style={{ marginBottom: 24 }}>
                <h3 style={{ fontSize: '16px', fontWeight: 500, marginBottom: 16 }}>
                  {_$('脚本内容')}
                </h3>
                <div
                  style={{
                    border: '1px solid #e5e5e5',
                    borderRadius: '4px',
                    overflow: 'hidden'
                  }}
                >
                  <pre
                    style={{
                      padding: '12px',
                      backgroundColor: '#f5f5f5',
                      fontSize: '12px',
                      overflow: 'auto',
                      maxHeight: '400px',
                      margin: 0,
                      whiteSpace: 'pre-wrap',
                      wordBreak: 'break-word'
                    }}
                  >
                    {functionDetail.Content}
                  </pre>
                </div>
              </div>
            )}

            {/* 示例代码 */}
            {example && (
              <div style={{ marginBottom: 24 }}>
                <h3 style={{ fontSize: '16px', fontWeight: 500, marginBottom: 16 }}>
                  {_$('示例')}
                </h3>
                <ExampleCodeViewer example={example} lang={codeMode} />
              </div>
            )}
          </div>
        </Card>
        <div className="button_row">
          <Button
            type="danger"
            disabled={!approving}
            onClick={() => {
              onApplyAudit(-1)
            }}
          >
            {_$('驳回')}
          </Button>
          <Button
            disabled={!approving}
            type="primary"
            onClick={() => {
              onApplyAudit(2)
            }}
          >
            {_$('通过')}
          </Button>
        </div>
      </CustomPageContainer>
    </div>
  )
}

export default styled(Approve)`
  padding-bottom: 64px;
  .button_row {
    position: fixed;
    right: 0;
    bottom: 0;
    background-color: #fff;
    height: 64px;
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: flex-end;
    padding: 0 16px;
    .k-btn {
      margin-left: 8px;
    }
  }
`
